import { ChevronDown, FileText } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { kgToMt, formatDate, formatNumber } from "@/lib/format"
import { cn } from "@/lib/utils"
import { paths } from "@/routes/paths"
import type { Contract, Cooperative, OrderDetail } from "@/types/domain"

function Field({
  label,
  children,
  fullWidth,
}: {
  label: string
  children: ReactNode
  fullWidth?: boolean
}) {
  return (
    <div
      className={cn(
        "bg-muted/40 flex flex-col gap-0.5 rounded-md p-3",
        fullWidth ? "sm:col-span-2" : "",
      )}
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm font-medium">{children || "—"}</span>
    </div>
  )
}

interface OrderDetailsSectionProps {
  order: OrderDetail
  contract: Contract
  cooperatives: Cooperative[] | undefined
}

export function OrderDetailsSection({
  order,
  contract,
  cooperatives,
}: OrderDetailsSectionProps) {
  const { t, i18n } = useTranslation(["orders", "contracts", "common"])
  const [open, setOpen] = useState(true)

  const cooperativeName =
    cooperatives?.find((c) => String(c.id) === String(order.groupId))?.name ?? ""

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="text-muted-foreground size-4" />
          {t("details.title")}
        </CardTitle>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown
            className={cn("size-4 transition-transform", open ? "rotate-180" : "")}
          />
        </Button>
      </CardHeader>
      {open ? (
        <CardContent className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-muted-foreground text-sm font-semibold">
              {t("details.info")}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field label={t("details.fields.cooperative")}>
                {order.groupId ? (
                  <Link
                    to={paths.cooperativeDetail(String(order.groupId))}
                    className="text-primary hover:underline"
                  >
                    {cooperativeName || `#${order.groupId}`}
                  </Link>
                ) : null}
              </Field>
              <Field label={t("details.fields.contract")}>
                <Link
                  to={paths.contractDetail(contract.id)}
                  className="text-primary hover:underline"
                >
                  {contract.contractNumber ?? `#${contract.id}`}
                </Link>
              </Field>
              <Field label={t("details.fields.client")}>{contract.clientName}</Field>
              <Field label={t("details.fields.product")}>
                {contract.productType
                  ? t(`options.productTypes.${contract.productType}`, {
                      ns: "contracts",
                      defaultValue: contract.productType,
                    })
                  : null}
              </Field>
              <Field label={t("details.fields.quantity")}>
                {formatNumber(kgToMt(order.agreedQuantity ?? 0))}{" "}
                {t("units.mt", { ns: "common" })}
              </Field>
              <Field label={t("details.fields.createdAt")}>
                {formatDate(order.created_at, i18n.language)}
              </Field>
              {order.contractType === "SPOT_CONTRACTS" ? (
                <Field label={t("details.fields.deadline")}>
                  {formatDate(order.dateOfExportToPort, i18n.language)}
                </Field>
              ) : (
                <Field label={t("details.fields.deliveryPeriod")} fullWidth>
                  {formatDate(order.contractDurationStart, i18n.language)}{" "}
                  – {formatDate(order.contractDurationEnd, i18n.language)}
                </Field>
              )}
              <Field label={t("details.fields.contractType")}>
                {order.contractType
                  ? t(`options.contractTypes.${order.contractType}`, {
                      ns: "contracts",
                      defaultValue: order.contractType,
                    })
                  : null}
              </Field>
              <Field label={t("details.fields.deliveryAddress")} fullWidth>
                {order.deliveryAddress}
              </Field>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-muted-foreground text-sm font-semibold">
              {t("details.commercial")}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field label={t("details.fields.farmgatePrice")}>
                {formatNumber(order.basePrice ?? 0, 2)} CFA{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
              <Field label={t("details.fields.premium")}>
                {formatNumber(order.agroforestryBonusPerKilo ?? 0, 2)} CFA{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            </div>

            <h3 className="text-muted-foreground mt-4 text-sm font-semibold">
              {t("details.certifications")}
            </h3>
            <div className="bg-muted/40 rounded-md p-3">
              <span className="text-muted-foreground text-xs">
                {t("details.fields.requiredCerts")}
              </span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(contract.certificates ?? []).length === 0 ? (
                  <span className="text-muted-foreground text-sm">—</span>
                ) : (
                  (contract.certificates ?? []).map((cert) => (
                    <StatusBadge key={cert} tone="success">
                      {t(`options.certificates.${cert}`, {
                        ns: "contracts",
                        defaultValue: cert,
                      })}
                    </StatusBadge>
                  ))
                )}
              </div>
            </div>

            {order.qualityCriteria ? (
              <div>
                <h3 className="text-muted-foreground mb-2 text-sm font-semibold">
                  {t("details.notes")}
                </h3>
                <p className="text-sm">{order.qualityCriteria}</p>
              </div>
            ) : null}
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}
